import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ThemeContext = createContext({ theme: "dark", branding: null, setTheme: () => {} });

const THEME_PRESETS = {
  dark: {
    "--background": "0 0% 4%", "--foreground": "0 0% 100%",
    "--card": "0 0% 7%", "--card-foreground": "0 0% 100%",
    "--popover": "0 0% 7%", "--popover-foreground": "0 0% 100%",
    "--primary": "211 100% 50%", "--primary-foreground": "0 0% 100%",
    "--secondary": "0 0% 15%", "--secondary-foreground": "0 0% 100%",
    "--muted": "0 0% 15%", "--muted-foreground": "0 0% 64%",
    "--accent": "211 100% 50%", "--accent-foreground": "0 0% 100%",
    "--destructive": "0 100% 60%", "--destructive-foreground": "0 0% 100%",
    "--border": "0 0% 15%", "--input": "0 0% 15%", "--ring": "211 100% 50%",
  },
  light: {
    "--background": "0 0% 98%", "--foreground": "0 0% 10%",
    "--card": "0 0% 100%", "--card-foreground": "0 0% 10%",
    "--popover": "0 0% 100%", "--popover-foreground": "0 0% 10%",
    "--primary": "211 100% 50%", "--primary-foreground": "0 0% 100%",
    "--secondary": "0 0% 92%", "--secondary-foreground": "0 0% 10%",
    "--muted": "0 0% 92%", "--muted-foreground": "0 0% 40%",
    "--accent": "211 100% 50%", "--accent-foreground": "0 0% 100%",
    "--destructive": "0 100% 60%", "--destructive-foreground": "0 0% 100%",
    "--border": "0 0% 85%", "--input": "0 0% 85%", "--ring": "211 100% 50%",
  },
  "high-contrast": {
    "--background": "0 0% 0%", "--foreground": "0 0% 100%",
    "--card": "0 0% 5%", "--card-foreground": "0 0% 100%",
    "--popover": "0 0% 5%", "--popover-foreground": "0 0% 100%",
    "--primary": "60 100% 50%", "--primary-foreground": "0 0% 0%",
    "--secondary": "0 0% 12%", "--secondary-foreground": "0 0% 100%",
    "--muted": "0 0% 12%", "--muted-foreground": "0 0% 75%",
    "--accent": "60 100% 50%", "--accent-foreground": "0 0% 0%",
    "--destructive": "0 100% 55%", "--destructive-foreground": "0 0% 100%",
    "--border": "0 0% 30%", "--input": "0 0% 12%", "--ring": "60 100% 50%",
  },
  navy: {
    "--background": "222 47% 8%", "--foreground": "210 40% 96%",
    "--card": "222 47% 11%", "--card-foreground": "210 40% 96%",
    "--popover": "222 47% 11%", "--popover-foreground": "210 40% 96%",
    "--primary": "199 89% 48%", "--primary-foreground": "222 47% 8%",
    "--secondary": "222 47% 16%", "--secondary-foreground": "210 40% 96%",
    "--muted": "222 47% 16%", "--muted-foreground": "215 20% 55%",
    "--accent": "199 89% 48%", "--accent-foreground": "222 47% 8%",
    "--destructive": "0 84% 60%", "--destructive-foreground": "0 0% 100%",
    "--border": "222 47% 18%", "--input": "222 47% 16%", "--ring": "199 89% 48%",
  },
  emerald: {
    "--background": "160 30% 5%", "--foreground": "150 20% 95%",
    "--card": "160 30% 8%", "--card-foreground": "150 20% 95%",
    "--popover": "160 30% 8%", "--popover-foreground": "150 20% 95%",
    "--primary": "160 84% 39%", "--primary-foreground": "0 0% 100%",
    "--secondary": "160 30% 14%", "--secondary-foreground": "150 20% 95%",
    "--muted": "160 30% 14%", "--muted-foreground": "155 10% 55%",
    "--accent": "160 84% 39%", "--accent-foreground": "0 0% 100%",
    "--destructive": "0 84% 60%", "--destructive-foreground": "0 0% 100%",
    "--border": "160 20% 16%", "--input": "160 30% 14%", "--ring": "160 84% 39%",
  },
  crimson: {
    "--background": "0 20% 5%", "--foreground": "0 10% 95%",
    "--card": "0 20% 8%", "--card-foreground": "0 10% 95%",
    "--popover": "0 20% 8%", "--popover-foreground": "0 10% 95%",
    "--primary": "0 72% 51%", "--primary-foreground": "0 0% 100%",
    "--secondary": "0 15% 14%", "--secondary-foreground": "0 10% 95%",
    "--muted": "0 15% 14%", "--muted-foreground": "0 10% 55%",
    "--accent": "0 72% 51%", "--accent-foreground": "0 0% 100%",
    "--destructive": "30 90% 55%", "--destructive-foreground": "0 0% 100%",
    "--border": "0 15% 18%", "--input": "0 15% 14%", "--ring": "0 72% 51%",
  },
};

export const THEME_OPTIONS = [
  { value: "dark", label: "Dark" },
  { value: "light", label: "Light" },
  { value: "high-contrast", label: "High Contrast" },
  { value: "navy", label: "Navy" },
  { value: "emerald", label: "Emerald" },
  { value: "crimson", label: "Crimson" },
];

/** Convert HEX (#RRGGBB) to HSL string "H S% L%" for CSS variables */
function hexToHsl(hex) {
  if (!hex || !hex.startsWith("#") || hex.length < 7) return null;
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Determine if a color is "light" for foreground contrast */
function isLightColor(hex) {
  if (!hex || !hex.startsWith("#") || hex.length < 7) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
}

/** Load a Google Font dynamically */
function loadGoogleFont(fontName) {
  if (!fontName) return;
  const id = `gfont-${fontName.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

/** Remove old custom CSS style tag and inject new one */
function applyCustomCss(css) {
  const id = "custom-admin-css";
  let tag = document.getElementById(id);
  if (!css) {
    if (tag) tag.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("style");
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}

/** Set favicon dynamically */
function applyFavicon(url) {
  if (!url) return;
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  if (link.href !== url) link.href = url;
}

function applyTheme(theme, branding) {
  const root = document.documentElement;
  const vars = THEME_PRESETS[theme] || THEME_PRESETS.dark;

  // Apply base theme preset
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  if (!branding) return;

  // Override primary color
  if (branding.primary_color) {
    const hsl = hexToHsl(branding.primary_color);
    if (hsl) {
      root.style.setProperty("--primary", hsl);
      root.style.setProperty("--ring", hsl);
      // Only override accent if no explicit accent_color is set
      if (!branding.accent_color) {
        root.style.setProperty("--accent", hsl);
      }
      const fg = isLightColor(branding.primary_color) ? "0 0% 0%" : "0 0% 100%";
      root.style.setProperty("--primary-foreground", fg);
    }
  }

  // Override accent color
  if (branding.accent_color) {
    const hsl = hexToHsl(branding.accent_color);
    if (hsl) {
      root.style.setProperty("--accent", hsl);
      const fg = isLightColor(branding.accent_color) ? "0 0% 0%" : "0 0% 100%";
      root.style.setProperty("--accent-foreground", fg);
    }
  }

  // Override sidebar/card color
  if (branding.sidebar_color) {
    const hsl = hexToHsl(branding.sidebar_color);
    if (hsl) {
      root.style.setProperty("--card", hsl);
      root.style.setProperty("--popover", hsl);
      const fg = isLightColor(branding.sidebar_color) ? "0 0% 10%" : "0 0% 100%";
      root.style.setProperty("--card-foreground", fg);
      root.style.setProperty("--popover-foreground", fg);
    }
  }

  // Override text/foreground color
  if (branding.text_color) {
    const hsl = hexToHsl(branding.text_color);
    if (hsl) {
      root.style.setProperty("--foreground", hsl);
    }
  }

  // Apply fonts
  if (branding.heading_font) {
    loadGoogleFont(branding.heading_font);
    root.style.setProperty("--font-heading", `'${branding.heading_font}', sans-serif`);
  }
  if (branding.body_font) {
    loadGoogleFont(branding.body_font);
    root.style.setProperty("--font-body", `'${branding.body_font}', sans-serif`);
  }

  // Apply custom CSS
  applyCustomCss(branding.custom_css || "");

  // Apply favicon
  if (branding.favicon_url) applyFavicon(branding.favicon_url);
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState("dark");
  const [branding, setBranding] = useState(null);

  const fetchBranding = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/branding`);
      setBranding(data);
      const t = data.theme || "dark";
      setTheme(t);
      applyTheme(t, data);
      if (data.system_name) document.title = data.system_name;
    } catch (err) {
      console.error("Failed to load branding:", err);
      applyTheme("dark", null);
    }
  }, []);

  useEffect(() => { fetchBranding(); }, [fetchBranding]);
  useEffect(() => { applyTheme(theme, branding); }, [theme, branding]);

  const value = useMemo(() => ({ theme, setTheme, branding, setBranding }), [theme, branding]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useThemeContext() {
  return useContext(ThemeContext);
}
